/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */

import _forEach from 'lodash/forEach';
import ChartConst from '../../../constants/chart.constant';
import DefConst from '../../../constants/def.constant';
import AppConst from '../../../constants/app.constant';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import { is_iphone } from '../../../services/utils/is_iphone';
import { is_ipad } from '../../../services/utils/is_ipad';
import bnChartDataProcessService from '../../../services/chartDataProcess.service';
import d3 from '../../../globalexports/d3.export';
import { validateAloftData } from '../../../services/validation/validateAloftData';

function routeTrackLineService() {
  const content = {};
  let trendsForHide;
  let barbForHide;
  let airportStoprForHide;
  let canvasLoaded = false;
  content.processElevation = function (allCharts, sourceData) {
    const xValues = [];
    const elvValues = [];
    sourceData.forEach((item) => {
      xValues.push(allCharts.ceilingChart.axisInfo.x(item.arrival_time));
      elvValues.push(item.airportDetails.el);
    });
    return {
      xValues,
      elvValues,
    };
  };
  content.drawElevation = function (
    fullData,
    elvX,
    bigChartHeight,
    allCharts,
    trendLineContainer,
    ceilingMaxValueAllowed,
    xAxisTopContainerHeight,
    flightWeatherStripHeight,
    rulesetStripHeight,
    sourceData,
  ) {
    if (fullData.elevationData) {
      const elvProcessedData = content.processElevation(allCharts, sourceData);
      const areaElv = d3.area()
        .x((...args) => {
          const key = args[1];
          return elvX(key);
        })
        .y0(bigChartHeight)
        .y1((d, key) => {
          const keyX = elvX(key);
          if (Math.abs(keyX - elvProcessedData.xValues[0]) < 1) {
            const [elvValueToUse] = elvProcessedData.elvValues;
            d.elevation = elvValueToUse;
            elvProcessedData.xValues.shift();
            elvProcessedData.elvValues.shift();
          }
          return allCharts.ceilingChart.axisInfo.y1(Math.max(Math.min(
            d.elevation,
            ceilingMaxValueAllowed,
          ), 0));
        });
      trendLineContainer.append('g')
        .attr('id', 'route-elevation-area')
        .attr(
          'transform',
          `translate(0, ${xAxisTopContainerHeight + flightWeatherStripHeight + rulesetStripHeight})`,
        )
        .append('path')
        .data([fullData.elevationData])
        .attr('class', 'area')
        .attr('fill', ChartConst.ChartLabelSpecs.elv.color)
        .attr('d', areaElv);
    }
  };
  content.drawTrackLine = function (
    settings,
    majorContainerHeight,
    chartHeight,
    width,
    allCharts,
    sourceData,
    topSpace,
    margin,
    ceilingChartY,
    bigChartHeight,
    rulesetStripHeight,
    flightWeatherStripHeight,
    xAxisBottomContainerHeight,
    xAxisTopContainerHeight,
    yAxisRectHeight,
    temperatureStripHeight,
    yAxisRectWidth,
    yAxisRightRectWidth,
    aloftData,
    ceilingChartAxis,
    trendSvg,
    ceilingMaxValueAllowed,
    ceilingMinValueAllowed,
    fullData,
    softDraw,
  ) {
    content.detachtrendlineEvents();
    const normalTextHeight = bnd3Service.getSelectionHeight(d3.select('#bntextrefroute'));
    const yposMain = {};
    const topMarginFactor = flightWeatherStripHeight + rulesetStripHeight;
    const rectRadius = 2;
    const viewPrefix = 'route-';
    const hideBottomAxis = utilityService.hideBottomAxis();
    const elvX = d3.scaleLinear()
      .range([yAxisRectWidth, width - yAxisRightRectWidth])
      .unknown(yAxisRectWidth);
    elvX.domain([0, AppConst.elevationSamples]);
    const flightDots = is_iphone()
      ? ChartConst.flightDotsMobile
      : ChartConst.flightDots;
    let dataCollection;
    let flightDirection;
    let defaultWindSpeed;
    if (!softDraw) {
      // Combining all chart data to single object to render trend line
      dataCollection = utilityService.clone(allCharts.ceilingChart.data);
      if (!hideBottomAxis) {
        if (allCharts.precepitationChart.data) {
          _forEach(allCharts.precepitationChart.data, (data) => {
            dataCollection.push(data);
          });
        }
      }
      // finding flight direction to rotate degree
      flightDirection = utilityService.getPlaneDirection(sourceData[0], sourceData[sourceData
        .length - 1]);
    }
    let altitudeData = DefConst[`aloftAltitudeMap${AppConst.dataMode.routeprofile}`];
    const barbOpacity = ChartConst.aloftBarbOpacityShowBarb;
    if (is_iphone()) {
      defaultWindSpeed = ChartConst.windSpeed.mobileLight;
    } else if (is_ipad()) {
      defaultWindSpeed = ChartConst.windSpeed.tabletLight;
    } else {
      defaultWindSpeed = ChartConst.windSpeed.light;
    }
    let planeConfig;
    if (utilityService.is_small_device()) {
      planeConfig = ChartConst.planeConfigMobile;
    } else {
      planeConfig = ChartConst.planeConfig;
    }
    const trackLineLength = majorContainerHeight - xAxisTopContainerHeight
      - xAxisBottomContainerHeight - temperatureStripHeight - (topSpace / 2);

    const trendLineContainer = trendSvg.append('g')
      .attr('id', 'route-trendLineContainer')
      .attr('transform', `translate(${margin.left}, ${0})`);
    // function bringElementToFront(id) {
    //     // d3.selectAll("#" + id).each(function () {
    //     //     var selfEle = utilityService.getSelfElement(arguments);
    //     //     if (selfEle && selfEle.parentNode) {
    //     //         selfEle.parentNode.appendChild(selfEle);
    //     //     }
    //     // });
    // }
    let altitudeContainer;

    function addAltitudeComponent() {
      altitudeContainer = trendLineContainer.append('g')
        .attr('class', 'mouse-move-tracker part-of-tooltip')
        .attr('id', 'altitudeContainer')
        .style('display', 'none');
      altitudeContainer.append('path')
        .attr('d', ChartConst.planePath);
      if (validateAloftData(aloftData)) {
        const aloftWindBarbsContainer = trendLineContainer.append('g')
          .attr('id', 'route-barbs-container')
          .attr('class', 'aloftWindBarbsContainer')
          .attr('barbOpacity', barbOpacity)
          .attr('transform', `translate(0, ${xAxisTopContainerHeight + topMarginFactor})`);
        // Creating marker for barbhead
        bnd3Service.addBarbMarker(
          aloftWindBarbsContainer,
          'route-barbheadLight-aloft',
          defaultWindSpeed,
          true,
        );
        bnd3Service.drawAloftWindBarbs(
          ceilingMinValueAllowed,
          settings.maximumAltitudeAllowed,
          aloftData,
          altitudeData,
          defaultWindSpeed,
          aloftWindBarbsContainer,
          ceilingChartAxis
            .x,
          ceilingChartAxis.y1,
          'arrival_time',
          viewPrefix,
          settings.showHeadWindOnly,
          settings,
        );
        // bringElementToFront("route-elevation-area");
      }
      content.drawElevation(
        fullData,
        elvX,
        bigChartHeight,
        allCharts,
        trendLineContainer,
        ceilingMaxValueAllowed,
        xAxisTopContainerHeight,
        flightWeatherStripHeight,
        rulesetStripHeight,
        sourceData,
      );

      if (!softDraw) {
        // This will be horizontal altitude line
        const horizontalAltitudeLine = trendLineContainer.append('g')
          .attr('class', 'horizontal-altitude-line part-of-tooltip')
          .attr('id', 'horizontalAltitudeLine')
          .style('display', 'none');
        horizontalAltitudeLine.append('line')
          .attr('id', 'horizontal-altitude-line')
          .attr('x1', yAxisRectWidth)
          .attr('y1', 0)
          .attr('x2', width - yAxisRightRectWidth)
          .attr('y2', 0);
        const horizontalAltitudeTooltipContainer = horizontalAltitudeLine.append('g')
          .attr('id', 'horizontalAltitudeTooltipContainer')
          .attr('class', 'horizontal-altitude-line-route-tooltip');
        horizontalAltitudeTooltipContainer.append('rect')
          .attr('height', normalTextHeight)
          .attr('rx', rectRadius)
          .attr('ry', rectRadius);
        horizontalAltitudeTooltipContainer.append('text')
          .style('text-anchor', 'start')
          .style('fill', '#fff');
      }
    }
    if (softDraw) {
      addAltitudeComponent();
      return;
    }
    addAltitudeComponent();
    const trends = trendLineContainer.selectAll('group')
      .data(allCharts.ceilingChart.data[0].values)
      .enter();

    const trend = trends.append('g')
      .attr('class', 'route-trend-line part-of-tooltip')
      .attr('transform', (d) => `translate(${allCharts.ceilingChart.axisInfo.x(d.x)}, ${xAxisTopContainerHeight})`)
      .attr('id', (...args) => {
        const key = args[1];
        return `route-trend-${key}`;
      })
      .attr('valueindex', (...args) => {
        const key = args[1];
        return key;
      })
      .attr('tooltipWestGravity', (d) => !!(allCharts.ceilingChart.axisInfo.x(d.x) > (window
        .innerWidth / 2)));
    if (utilityService.isFirefoxOrChrome()) {
      trend.style('opacity', '0');
    } else {
      trend.style('display', 'none');
    }
    const flightRuleTooltips = trend.append('g')
      .attr('class', 'flightRuleTooltips');

    flightRuleTooltips.append('rect')
      .attr('fill', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        return sourceData[valueIndex]
          ? bnd3Service.getFlightDotColorForRruleset(sourceData[valueIndex].frlServerValue
            || sourceData[valueIndex].frl)
          : '';
      });
    flightRuleTooltips.append('text')
      .style('stroke', '#fff')
      .style('stroke-width', '0px')
      .attr('x', 2)
      .style('fill', '#fff')
      .text((...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        return sourceData[valueIndex]
          ? `${bnd3Service.getFlightDotTextForRruleset(sourceData[valueIndex].frlServerValue || sourceData[valueIndex].frl)}, ${utilityService.getCloudText(sourceData[valueIndex].cld) || '--'}`
          : '';
      });

    flightRuleTooltips.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text')) + 4;
      })
      .attr('height', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionHeight(d3.select(selfEle.parentNode).select('text'));
      });
    flightRuleTooltips.attr('transform', (...args) => {
      const selfEle = utilityService.getSelfElement(args);
      let x = (flightDots.radius + flightDots.strokeWidth);
      const y = (flightWeatherStripHeight + ((rulesetStripHeight - normalTextHeight) / 2));
      const tooltipWestGravityValue = !!(d3.select(selfEle.parentNode).attr(
        'tooltipWestGravity',
      ) === 'true');
      if (tooltipWestGravityValue) {
        x -= bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text')) + 4
          + (flightDots.radius * 2) + (flightDots.strokeWidth * 2);
      }
      return `translate(${x}, ${y})`;
    });
    flightRuleTooltips.select('text')
      .attr('y', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        const tooltipTextHeight = parseFloat(d3.select(selfEle.parentNode).select('text').style(
          'font-size',
        ));
        return tooltipTextHeight - 0.5;
      });
    trend.append('line')
      .attr('class', 'path-trend-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', topMarginFactor)
      .attr('y2', trackLineLength);

    const trendPerLine = trend.selectAll('.trend-per-line-route')
      .data(dataCollection.filter((item) => item))
      .enter()
      .append('g')
      .attr('class', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        if (d.values[valueIndex]) {
          return `trend-per-line-route trend-per-line-route-${d.group}${d.values[valueIndex].hide
            ? '-hide'
            : ''}`;
        }
        return '';
      })
      .style('opacity', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        if (d.values[valueIndex]) {
          return (d.secondary && d.values[valueIndex].hide)
            ? 0
            : 1;
        }
        return 0;
      })
      .attr('data-value', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
        if (d.values[valueIndex]) {
          return `${d.key}-${d.values[valueIndex]
            ? d.values[valueIndex].y
            : null}`;
        }
        return '';
      });
    trendPerLine.append('circle')
      .attr('class', 'line-value-circle')
      .attr('r', 3)
      .attr('isAreaSeries', (d) => d.area)
      .attr('isSecondary', (d) => d.secondary)
      .attr('seriesKey', (d) => d.key)
      .style('stroke', (d) => d.color);
    const trendLineTooltips = trendPerLine.append('g')
      .attr('class', 'trend-tooltip-container-route')
      .attr('id', (...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(
          d3.select(selfEle.parentNode.parentNode).attr('valueindex'),
          10,
        );
        if (d.values[valueIndex]) {
          return `trend-tooltip-container-route-${valueIndex}${d.values[valueIndex].hide
            ? '-hide'
            : ''}`;
        }
        return '';
      });

    trendLineTooltips.append('rect')
      .attr('class', 'tooltip-value-rect')
      .attr('transform', 'translate(5, -10)');

    const textNode = trendLineTooltips.append('text')
      .text((...args) => {
        const d = args[0];
        const selfEle = utilityService.getSelfElement(args);
        const valueIndex = parseInt(d3.select(selfEle.parentNode.parentNode.parentNode).attr(
          'valueindex',
        ), 10);
        const dMain = d.values[valueIndex];
        const tooltipParts = dMain
          ? bnChartDataProcessService.formatTooltipText(d, dMain, settings)
          : '';
        if (tooltipParts === 'string') {
          return tooltipParts;
        }
        d.tooltipParts = tooltipParts;
        return `${tooltipParts[0]}:`;
      })
      .style('fill', (d) => d.color);
    textNode.append('tspan').attr('class', 'boldtext').text((...args) => {
      const d = args[0];
      const selfEle = utilityService.getSelfElement(args);
      const valueIndex = parseInt(d3.select(selfEle.parentNode.parentNode.parentNode
        .parentNode).attr('valueindex'), 10);
      const dMain = d.values[valueIndex];
      if (dMain) {
        const tooltipParts = dMain
          ? bnChartDataProcessService.formatTooltipText(d, dMain, settings)
          : '';
        if (tooltipParts[1]) {
          return tooltipParts[1];
        }
      }
      return '';
    });
    trendLineTooltips.select('rect')
      .attr('width', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select('text')) + 4;
      })
      .attr('height', (...args) => {
        const selfEle = utilityService.getSelfElement(args);
        return bnd3Service.getSelectionHeight(d3.select(selfEle.parentNode).select('text'));
      });

    if (validateAloftData(aloftData)) {
      const aloftWindBarbsTooltipContainer = trendLineContainer.append('g')
        .attr('id', 'route-barbs-tooltip-container')
        .attr('class', 'aloftWindBarbsTooltipContainer')
        .attr('barbOpacity', barbOpacity)
        .attr('transform', `translate(0, ${xAxisTopContainerHeight + topMarginFactor})`);
      bnd3Service.drawAloftWindBarbsTooltips(
        ceilingMinValueAllowed,
        settings
          .maximumAltitudeAllowed,
        aloftData,
        altitudeData,
        aloftWindBarbsTooltipContainer,
        ceilingChartAxis.x,
        ceilingChartAxis.y1,
        'arrival_time',
        viewPrefix,
        flightDots,
        defaultWindSpeed,
        settings.showHeadWindOnly,
        settings,
        normalTextHeight,
      );
    }

    function shiftAreaCirclePoints(...args) {
      const selfEle = utilityService.getSelfElement(args);
      const x = 0;
      let y;
      if (d3.select(selfEle).attr('isAreaSeries') === 'true') {
        y = utilityService.getAdditionalShiftToHandleAreaSeries(d3.select(selfEle).attr(
          'seriesKey',
        ));
        return `translate(${x}, ${y})`;
      }
    }
    trendPerLine.attr('transform', (...args) => {
      const d = args[0];
      const i = args[1];
      const selfEle = utilityService.getSelfElement(args);
      const valueIndex = parseInt(d3.select(selfEle.parentNode).attr('valueindex'), 10);
      const tooltipWestGravityValue = !!(d3.select(selfEle.parentNode).attr(
        'tooltipWestGravity',
      ) === 'true');
      const chartPositionFactor = topMarginFactor;
      const dMain = d.values[valueIndex];
      if (!dMain) {
        return null;
      }
      const yPosObj = {};
      let position;
      if (!yposMain[`#trend-tooltip-container-route-${valueIndex.toString()}`]) {
        yposMain[`#trend-tooltip-container-route-${valueIndex.toString()}`] = {
          ypos: {
            tooltipWestGravity: tooltipWestGravityValue,
            valueIndex,
            group1: [],
            group2: [],
            group3: [],
            group4: [],
          },
        };
      }
      if (d.group === 1) {
        // For Ceiling chart
        if (d.key === 'WSP') {
          yPosObj.y = bigChartHeight - 2;
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
        if (d.key === 'WGS') {
          yPosObj.y = bigChartHeight - 2;
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else if (d.yAxis === 1) {
          yPosObj.y = allCharts.ceilingChart.axisInfo.y1(dMain.y > settings
            .maximumAltitudeAllowed
            ? settings.maximumAltitudeAllowed
            : dMain.y || 0);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        } else {
          // for visiblity dMain.v has ceiling_mapped value
          // Using math.min to make sure for 6k visibility does not leak outside chart
          yPosObj.y = Math.max(allCharts.ceilingChart.axisInfo.y1(Math.min(dMain.v, ChartConst
            .visibilityTillWhatAltitude) / 2) - (sourceData[valueIndex]
            ? sourceData[valueIndex].visRectElvOffset
            : 0), 0);
          position = `translate(0,${yPosObj.y + chartPositionFactor})`;
        }
      }
      if (d.group === 2) {
        // For Precepitation chart
        if (d.yAxis === 1) {
          yPosObj.y = allCharts.precepitationChart.axisInfo.y1(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor + bigChartHeight + (topSpace / 2)})`;
        } else {
          yPosObj.y = allCharts.precepitationChart.axisInfo.y2(dMain.y);
          position = `translate(0,${yPosObj.y + chartPositionFactor + bigChartHeight + (topSpace / 2)})`;
        }
      }
      yPosObj.ind = i;
      yPosObj.off = 0;
      yPosObj.group = d.group;
      yPosObj.key = d.key;
      yPosObj.yValue = dMain.y;
      yPosObj.hide = dMain.hide;
      if (!yPosObj.hide) {
        yposMain[`#trend-tooltip-container-route-${valueIndex.toString()}`].ypos[
          `group${d.group}`].push(yPosObj);
      }
      return position;
    }).call(() => {
      const yposMainArray = Object.keys(yposMain);
      let yPosMainArrayIndex = 0;
      let yposObjKey;
      let toolTipContainerObj;
      let yposObj;
      while (yPosMainArrayIndex < yposMainArray.length) {
        yposObjKey = yposMainArray[yPosMainArrayIndex];
        toolTipContainerObj = d3.selectAll(yposObjKey);
        yposObj = yposMain[yposObjKey].ypos;
        if (yposMain[yposObjKey].ypos.group1[3] && yposMain[yposObjKey].ypos.group1[3].key
          === ChartConst.ChartLabelSpecs.wgs.key && yposMain[yposObjKey].ypos.group1[3].yValue
          === 0) {
          yposMain[yposObjKey].ypos.group1.splice(3);
        }
        yposMain[yposObjKey].ypos.group1 = bnd3Service.processGroup2Tips(yposMain[yposObjKey]
          .ypos.group1, bigChartHeight, toolTipContainerObj);
        yposMain[yposObjKey].ypos.group2 = bnd3Service.processGroup2Tips(yposMain[yposObjKey]
          .ypos.group2, chartHeight, toolTipContainerObj);
        // Group1
        bnd3Service.alignToolTips(
          `#route-trend-${yposObj.valueIndex} .trend-per-line-route-1`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );
        // Group2
        bnd3Service.alignToolTips(
          `#route-trend-${yposObj.valueIndex} .trend-per-line-route-2`,
          yposObj,
          toolTipContainerObj,
          yposObj.tooltipWestGravity,
        );

        // Shifting aria circle points
        d3.selectAll('.line-value-circle').attr('transform', shiftAreaCirclePoints);
        yPosMainArrayIndex += 1;
      }
    });

    function hideAllTrendLines() {
      if (!trendsForHide) {
        trendsForHide = d3.selectAll('.route-trend-line');
      }
      if (!barbForHide) {
        barbForHide = d3.selectAll('.route-aloft-barb');
      }
      if (!airportStoprForHide) {
        airportStoprForHide = d3.selectAll('.airport-stop');
      }
      if (barbForHide) {
        // Do this when barbs are drawn
        barbForHide.style('display', 'none');
      }
      trendsForHide.style('display', 'none');
      if (barbForHide) {
        barbForHide.style('display', 'none');
      }
      airportStoprForHide.select('rect').style('opacity', '0');
      airportStoprForHide.selectAll('text').style('fill', '#000');
      d3.select('#altitudeContainer').style('display', 'none');
      d3.select('#horizontalAltitudeLine').style('display', 'none');
    }

    function resetAloftBarbsOpacity() {
      d3.selectAll('.route-aloft-barb').style('opacity', '1')
        .style('display', 'none');
      d3.selectAll('.route-barb-path')
        .style('fill', defaultWindSpeed.color)
        .style('stroke', defaultWindSpeed.color);
      d3.select('#route-barbheadLight-aloft-polyline').attr('fill', defaultWindSpeed.color);
    }

    function svgToCanvas() {
      d3.selectAll('.route-aloft-barb')
        .style('display', 'inherit');
      const canvasWidth = width;
      const canvasHeight = bigChartHeight + topMarginFactor + xAxisTopContainerHeight;
      bnd3Service.convertSvgToCanvas(
        'route-barbs-container',
        'aloft-canvas-container-route',
        'aloftBarbsCanvas-route',
        canvasWidth,
        canvasHeight,
        resetAloftBarbsOpacity,
      );
      bnd3Service.aloftSuppress();
    }

    function loadCanvas() {
      if (!canvasLoaded) {
        canvasLoaded = true;
        svgToCanvas();
        // bringElementToFront("route-trendLineContainer");
        // bringElementToFront("trendLineTrackareaRoute");
      }
    }
    loadCanvas();
    // Positioning and rotating plane
    function positionPlane(flightDirectionOfPlane) {
      altitudeContainer.select('path').attr('transform', () => {
        const x = -planeConfig.offset;
        const y = -planeConfig.offset;
        const rotationDegree = `${settings.showHeadWindOnly
          ? 90
          : flightDirectionOfPlane} ${planeConfig.offset} ${planeConfig.offset}`; // Rotate clockwise
        return `translate(${x}, ${y}) rotate(${rotationDegree}) scale(${planeConfig.scale})`;
      });
    }
    positionPlane(flightDirection);

    function trendLineWorker(mouse, d3Event) {
      loadCanvas();
      const trackLineContainer = document.getElementById('trendLineTrackareaRoute');
      if (trackLineContainer) {
        // var mouse = d3.pointer(trackLineContainer);
        // This will handle notch
        mouse[0] -= margin.left;
        const xDate = allCharts.ceilingChart.axisInfo.x.invert(mouse[0]);
        const yAltitude = allCharts.ceilingChart.axisInfo.y1.invert((mouse[1]) - (ceilingChartY));
        const bisect = d3.bisector((d) => d.x).right;
        const altitudeBisect = d3.bisector((d) => d).right;
        let altitude;
        let altitudeNearestIdx;
        let altitudeMain;
        let nearestIdx;
        let dMain;
        // idx storing data index closest to mouse position
        const idx = bisect(allCharts.ceilingChart.data[0].values, xDate);
        altitudeData = DefConst[`aloftAltitudeMap${AppConst.dataMode.routeprofile}`];
        const altitudeIdx = altitudeBisect(altitudeData, yAltitude);
        // hide all the trendlines
        hideAllTrendLines();
        const d0 = allCharts.ceilingChart.data[0].values[idx - 1]; // Nearest value at left
        const d1 = allCharts.ceilingChart.data[0].values[idx]; // Nearest value at right
        const altitudeD0 = altitudeData[altitudeIdx - 1]; // Nearest value at left
        const altitudeD1 = altitudeData[altitudeIdx]; // Nearest value at right
        if (d0 || d1) {
          if (d0 && d1) {
            if (xDate - d0.x > d1.x - xDate) {
              nearestIdx = idx;
              dMain = d1;
            } else {
              nearestIdx = idx - 1;
              dMain = d0;
            }
          } else {
            dMain = d0 || d1;
            if (d0) {
              nearestIdx = idx - 1;
            } else {
              nearestIdx = idx;
            }
          }
          if (altitudeD0 >= 0 && altitudeD1 >= 0) {
            if (yAltitude - altitudeD0 > altitudeD1 - yAltitude) {
              altitudeNearestIdx = altitudeIdx;
              altitudeMain = altitudeD1;
              if ((allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight
                  + topMarginFactor) < (xAxisTopContainerHeight + topMarginFactor)) {
                altitudeMain = altitudeD0;
                altitudeNearestIdx = altitudeIdx - 1;
              }
            } else {
              altitudeNearestIdx = altitudeIdx - 1;
              altitudeMain = altitudeD0;
            }
          } else {
            altitudeMain = altitudeD0 || altitudeD1;
            if (altitudeD0) {
              altitudeNearestIdx = altitudeIdx - 1;
            } else {
              altitudeNearestIdx = altitudeIdx;
            }
          }

          // Finding altitude value at mouse Y coordinates
          // ToDO : find a way to limit mouse[1] i.e.y cordinate value for only ceiling chart
          altitude = allCharts.ceilingChart.axisInfo.y1.invert(mouse[1] - ceilingChartY).toFixed(
            0,
          );
          if (altitude >= ChartConst.ceilingDomain.min && altitude <= ceilingMaxValueAllowed) {
            // Doing math.min to make sure that 6k and 12K ft altitude get correct mid points
            // altitudeMain = altitudeMain;
            d3.selectAll(`.route-aloft-barb-group-${altitudeNearestIdx}`).style(
              'display',
              'inherit',
            );
            d3.selectAll(`.route-aloft-barb-group-${altitudeNearestIdx}-${nearestIdx}`).style('display', 'inherit');
            // If altitude value is under ceiling max/min domian
            d3.select('#altitudeContainer').style('display', 'inherit');
            d3.select('#horizontalAltitudeLine').style('display', 'inherit');

            d3.select('#horizontalAltitudeTooltipContainer').select('text').text(() => utilityService.abbreviateAltitudeCeiling(parseInt(altitudeMain, 10), ChartConst
              .ChartLabelSpecs.cig.tooltip.mobileUnit));
            d3.select('#horizontalAltitudeTooltipContainer').select('rect').attr('width', (...args) => {
              const selfEle = utilityService.getSelfElement(args);
              return bnd3Service.getSelectionWidth(d3.select(selfEle.parentNode).select(
                'text',
              )) + 3;
            });
            d3.select('#horizontalAltitudeTooltipContainer').select('text')
              .attr('y', '.35em')
              .attr('x', (...args) => {
                const selfEle = utilityService.getSelfElement(args);
                const barbPadding = utilityService.getBarbPadding();
                const rectWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
                return yAxisRectWidth - rectWidth - barbPadding - 1.5;
              });
            d3.select('#horizontalAltitudeTooltipContainer').select('rect')
              .attr('transform', (...args) => {
                const selfEle = utilityService.getSelfElement(args);
                const y = (-(normalTextHeight / 2));
                const barbPadding = utilityService.getBarbPadding();
                const rectWidth = bnd3Service.getSelectionWidth(d3.select(selfEle));
                const x = yAxisRectWidth - rectWidth - barbPadding;
                return `translate(${x}, ${y})`;
              });
            if (validateAloftData(aloftData)) {
              d3.select('#altitudeContainer').attr(
                'transform',
                `translate(${allCharts.ceilingChart.axisInfo.x(dMain.x)},${allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight + topMarginFactor})`,
              );
              d3.select('#horizontalAltitudeLine').attr(
                'transform',
                `translate(0 ,${allCharts.ceilingChart.axisInfo.y1(altitudeMain) + xAxisTopContainerHeight + topMarginFactor})`,
              );
            }
          } else {
            d3.select('#altitudeContainer').style('display', 'none');
            d3.select('#horizontalAltitudeLine').style('display', 'none');
          }
          // airportStops.select("rect").style("opacity", "0");
          // airportStops.selectAll("text").style("fill", "#000");
          positionPlane(sourceData[nearestIdx].flightDirection || 0);
          d3.selectAll(`.airportStop-${sourceData[nearestIdx].icao_id}`).select('rect').style('opacity', '1');
          d3.selectAll(`.airportStop-${sourceData[nearestIdx].icao_id}`).selectAll('text').style('fill', '#fff');
          d3.select(`#route-trend-${nearestIdx}`).style('display', 'inherit');
        }
        if (!utilityService.isCordovaApplication()) {
          if (d3Event) {
            // this will hide slider if opened on chart click
            bnd3Service.handleSliderToggleOnOutsideTap();
          } else if (window.event && window.event.type === 'click') {
            // this will hide slider if opened on chart click
            bnd3Service.handleSliderToggleOnOutsideTap();
          }
        } else {
          bnd3Service.handleSliderToggleOnOutsideTap();
        }
        if (d3Event) {
          d3Event.stopPropagation();
        } else if (window.event) {
          event.stopPropagation();
        }
      }
    }

    const trendLineTrackareaRoute = trendSvg.append('rect')
      .attr('id', 'trendLineTrackareaRoute')
      .attr('class', 'track-area')
      .attr('width', width - (yAxisRectWidth + yAxisRightRectWidth))
      .attr('height', yAxisRectHeight - rulesetStripHeight)
      .attr('x', yAxisRectWidth + margin.left)
      .attr('y', xAxisTopContainerHeight + flightWeatherStripHeight + rulesetStripHeight)
      .attr('fill', 'none');
    trendLineTrackareaRoute.on('click', (d3Event) => {
      const mouse = d3.pointer(d3Event, document.getElementById('trendLineTrackareaRoute'));
      window.requestAnimationFrame(() => {
        trendLineWorker(mouse, d3Event);
      });
    })
      .on('mousemove', (d3Event) => {
        const mouse = d3.pointer(d3Event, document.getElementById('trendLineTrackareaRoute'));
        window.requestAnimationFrame(() => {
          trendLineWorker(mouse, d3Event);
        });
      })
      .on('touchmove', (d3Event) => {
        const mouse = d3.pointer(d3Event.touches[0], document.getElementById('trendLineTrackareaRoute'));
        window.requestAnimationFrame(() => {
          trendLineWorker(mouse, d3Event);
        });
      });
    if (!utilityService.isCordovaApplication()) {
      trendLineTrackareaRoute.on('mouseout', () => {
        // Hide all trendlines when mouse out
        hideAllTrendLines();
      });
    }
    if (utilityService.isFirefoxOrChrome()) {
      trend.style('display', 'none');
      trend.style('opacity', '1');
    }
  };
  content.detachtrendlineEvents = function () {
    trendsForHide = null;
    barbForHide = null;
    airportStoprForHide = null;
    canvasLoaded = false;
  };
  return content;
}
export default routeTrackLineService();
// angular.module("metogramApp").service("routeTrackLineService", ["ChartConst",
//         "DefConst",
//         "AppConst",
//         "utilityService",
//         "bnChartDataProcessService",
//         "bnd3Service",
//         routeTrackLineService]);
